import { AlertTriangleIcon } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

export type SourceReferenceView = {
  chunkRerankScore?: number;
  isVerifiable?: boolean;
  sectionPath?: string[];
  source?: 'hybrid' | 'pageindex_tool';
};

type SourceReferencesProps = {
  className?: string;
  reference?: SourceReferenceView | null;
};

function formatSectionPath(sectionPath: string[]) {
  return sectionPath
    .map((segment) => segment.trim())
    .filter(Boolean)
    .join('\u202f›\u202f');
}

export function SourceReferences({ className, reference }: SourceReferencesProps) {
  if (!reference) {
    return null;
  }

  const breadcrumb = reference.sectionPath?.length ? formatSectionPath(reference.sectionPath) : '';
  const truncatedBreadcrumb = breadcrumb.length > 60 ? `${breadcrumb.slice(0, 57).trim()}...` : breadcrumb;
  const shouldShowExplorationBadge = reference.source === 'pageindex_tool';
  const shouldShowVerificationWarning = reference.isVerifiable === false;

  if (!breadcrumb && !shouldShowExplorationBadge && !shouldShowVerificationWarning) {
    return null;
  }

  return (
    <TooltipProvider>
      <div
        className={cn('flex min-w-0 flex-wrap items-center gap-1.5', className)}
        data-debug-score={reference.chunkRerankScore}
      >
        {breadcrumb ? (
          <p
            aria-label="Emplacement dans le document"
            className="min-w-0 max-w-full truncate text-xs text-muted-foreground"
            title={breadcrumb}
          >
            {truncatedBreadcrumb}
          </p>
        ) : null}

        {shouldShowExplorationBadge ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge
                variant="secondary"
                title="Cette source a été identifiée par exploration structurée du document."
              >
                Exploration approfondie
              </Badge>
            </TooltipTrigger>
            <TooltipContent>
              <p>Cette source a été identifiée par exploration structurée du document.</p>
            </TooltipContent>
          </Tooltip>
        ) : null}

        {shouldShowVerificationWarning ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge
                variant="destructive"
                title="Citation non vérifiée dans le document source."
              >
                <AlertTriangleIcon data-icon="inline-start" />
                Citation non vérifiée
              </Badge>
            </TooltipTrigger>
            <TooltipContent>
              <p>Citation non vérifiée dans le document source.</p>
            </TooltipContent>
          </Tooltip>
        ) : null}
      </div>
    </TooltipProvider>
  );
}
